import { Link } from "react-router-dom";
import { Phone, Check, Home, Sparkles, Truck, Building2, ChevronRight } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";

const services = [
  {
    id: "standard",
    icon: Home,
    title: "Standard House Cleaning",
    description: "Keep your home fresh and tidy with our regular cleaning service. Perfect for busy families who want to maintain a clean home without the hassle.",
    image: "https://images.pexels.com/photos/4239031/pexels-photo-4239031.jpeg?auto=compress&cs=tinysrgb&w=600",
    includes: [
      "Dusting all surfaces and furniture",
      "Vacuuming and mopping floors",
      "Kitchen counters and appliance exteriors",
      "Bathroom cleaning and sanitizing",
      "Trash removal and bed making",
      "General tidying and organizing",
    ],
    ideal: "Weekly or bi-weekly maintenance for busy households",
  },
  {
    id: "deep",
    icon: Sparkles,
    title: "Deep Cleaning",
    description: "A thorough, top-to-bottom clean that reaches every corner. Ideal for spring cleaning, special occasions, or when your home needs extra attention.",
    image: "https://images.pexels.com/photos/4239091/pexels-photo-4239091.jpeg?auto=compress&cs=tinysrgb&w=600",
    includes: [
      "Everything in standard cleaning PLUS:",
      "Inside oven and refrigerator cleaning",
      "Baseboards and door frames",
      "Light fixtures and ceiling fans",
      "Window sills and blinds",
      "Cabinet fronts and hardware",
      "Deep scrub of bathrooms and kitchen",
    ],
    ideal: "Seasonal deep cleans or first-time customers",
  },
  {
    id: "move",
    icon: Truck,
    title: "Move-In / Move-Out Cleaning",
    description: "Start fresh in your new home or leave your old place spotless. Our comprehensive move cleaning helps you get your full deposit back.",
    image: "https://images.pexels.com/photos/4107120/pexels-photo-4107120.jpeg?auto=compress&cs=tinysrgb&w=600",
    includes: [
      "Complete deep cleaning of all rooms",
      "Inside all cabinets and closets",
      "All appliances inside and out",
      "Windows and window tracks",
      "Garage sweep (if applicable)",
      "Light switch plates and outlets",
      "Wall spot cleaning",
    ],
    ideal: "Moving in or out of a home or apartment",
  },
  {
    id: "office",
    icon: Building2,
    title: "Office Cleaning",
    description: "Professional cleaning services for businesses of all sizes. A clean workspace boosts productivity and makes a great impression on clients.",
    image: "https://images.pexels.com/photos/4239036/pexels-photo-4239036.jpeg?auto=compress&cs=tinysrgb&w=600",
    includes: [
      "Desk and workstation cleaning",
      "Common area and break room cleaning",
      "Restroom sanitization",
      "Floor vacuuming and mopping",
      "Trash and recycling removal",
      "Window and glass cleaning",
      "Flexible scheduling (after hours available)",
    ],
    ideal: "Small to medium businesses in Lubbock area",
  },
];

export default function Services() {
  return (
    <div>
      {/* Hero */}
      <section className="bg-gradient-to-b from-sky-50 to-white py-16 md:py-24">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="max-w-3xl">
            <h1 className="font-heading text-4xl md:text-5xl font-bold text-slate-900 mb-6">
              Our Cleaning Services
            </h1>
            <p className="text-lg text-slate-600 leading-relaxed">
              From routine maintenance to deep cleaning, West Texas Sparkle offers professional 
              cleaning services tailored to your needs. Serving Lubbock, Wolfforth, and Levelland.
            </p>
          </div>
        </div>
      </section>

      {/* Services List */}
      <section className="py-16 md:py-24 bg-white" data-testid="services-list">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-16 md:space-y-24">
            {services.map((service, index) => (
              <div 
                key={service.id} 
                id={service.id}
                className={`grid lg:grid-cols-2 gap-8 lg:gap-12 items-center ${
                  index % 2 === 1 ? "lg:flex-row-reverse" : ""
                }`}
                data-testid={`service-${service.id}`}
              >
                <div className={index % 2 === 1 ? "lg:order-2" : ""}>
                  <div className="relative">
                    <div className="absolute -inset-4 bg-sky-100/50 rounded-3xl blur-xl"></div>
                    <img
                      src={service.image}
                      alt={service.title}
                      className="relative rounded-2xl shadow-lg w-full object-cover aspect-[4/3]"
                    />
                  </div>
                </div>
                <div className={index % 2 === 1 ? "lg:order-1" : ""}>
                  <div className="flex items-center gap-3 mb-4">
                    <div className="w-12 h-12 rounded-xl bg-sky-100 flex items-center justify-center">
                      <service.icon className="w-6 h-6 text-sky-600" />
                    </div>
                    <h2 className="font-heading text-2xl md:text-3xl font-bold text-slate-900">
                      {service.title}
                    </h2>
                  </div>
                  <p className="text-slate-600 leading-relaxed mb-6">
                    {service.description}
                  </p>
                  <Card className="bg-slate-50 border-0 mb-6">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-base font-semibold text-slate-900">
                        What's Included:
                      </CardTitle>
                    </CardHeader>
                    <CardContent>
                      <ul className="grid sm:grid-cols-2 gap-2">
                        {service.includes.map((item, i) => (
                          <li key={i} className="flex items-start gap-2 text-sm text-slate-600">
                            <Check className="w-4 h-4 text-sky-600 mt-0.5 flex-shrink-0" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </CardContent>
                  </Card>
                  <p className="text-sm text-slate-500 mb-6">
                    <strong>Ideal for:</strong> {service.ideal}
                  </p>
                  <Link to="/contact">
                    <Button 
                      className="bg-sky-600 hover:bg-sky-700 text-white rounded-full px-6"
                      data-testid={`get-quote-${service.id}`}
                    >
                      Get a Free Quote
                      <ChevronRight className="w-4 h-4 ml-1" />
                    </Button>
                  </Link>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 md:py-24 bg-slate-50">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="font-heading text-3xl md:text-4xl font-bold text-slate-900 mb-4">
            Not Sure Which Service You Need?
          </h2>
          <p className="text-slate-600 text-lg mb-8 max-w-2xl mx-auto">
            Give us a call and we'll help you find the perfect cleaning solution for your home or office.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a href="tel:8065558123">
              <Button 
                size="lg" 
                className="w-full sm:w-auto bg-sky-600 hover:bg-sky-700 text-white rounded-full h-14 px-8 text-lg font-semibold"
                data-testid="services-cta-call"
              >
                <Phone className="w-5 h-5 mr-2" />
                (806) 555-8123
              </Button>
            </a>
            <Link to="/contact">
              <Button 
                size="lg" 
                variant="outline"
                className="w-full sm:w-auto border-2 border-sky-200 hover:border-sky-300 hover:bg-sky-50 text-sky-700 rounded-full h-14 px-8 text-lg font-semibold"
                data-testid="services-cta-quote"
              >
                Request a Quote
              </Button>
            </Link>
          </div>
        </div>
      </section>
    </div>
  );
}
